import React, { useState, useEffect, useRef } from 'react';
import { Download, Share, X } from 'lucide-react';
import { isTauri } from '../utils/tauri';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;

interface PwaInstallBannerProps {
  /**
   * 표시만 억제한다(언마운트가 아니다). beforeinstallprompt는 페이지 로드 직후 한 번만
   * 발생하므로 조건부 마운트로 바꾸면 그 이벤트를 놓쳐 안드로이드에서 설치 버튼이 영영 안 뜬다.
   */
  suppressed?: boolean;
}

export const PwaInstallBanner: React.FC<PwaInstallBannerProps> = ({ suppressed = false }) => {
  const [show, setShow] = useState(false);
  const deferredPromptRef = useRef<any>(null);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Tauri, 이미 standalone(홈 화면 설치됨)이면 표시 안 함
    if (isTauri()) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if ((navigator as any).standalone) return; // iOS standalone

    // dismiss 기간 확인
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    }

    // iOS 감지
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIos(ios);

    if (ios) {
      // iOS는 beforeinstallprompt 없음 → 바로 안내 표시
      setShow(true);
      return;
    }

    // Android/Chrome: beforeinstallprompt 캡처
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;
    prompt.prompt();
    const result = await prompt.userChoice;
    if (result.outcome === 'accepted') {
      setShow(false);
    }
    deferredPromptRef.current = null;
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show || suppressed) return null;

  return (
    // 흐름 안 배너: fixed top-9(타이틀바 매직넘버)로 헤더를 덮던 것을 레이아웃 흐름에 넣는다 (ConnectionBanner와 같은 자리)
    <div role="region" aria-label="앱 설치 안내" className="shrink-0 mx-3 mt-2 md:mx-auto md:w-96 rounded-xl shadow-sm border overflow-hidden bg-white border-slate-200 animate-slide-down">
      <div className="px-4 py-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            {isIos ? (
              <Share size={18} className="text-brand-600 shrink-0" />
            ) : (
              <Download size={18} className="text-brand-600 shrink-0" />
            )}
            <span className="text-sm font-semibold text-gray-900">앱 설치 안내</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 -m-1.5 rounded-lg hover:bg-black/10 transition-colors shrink-0 text-gray-400 hover:text-gray-600"
            title="닫기"
          >
            <X size={14} />
          </button>
        </div>
        <div className="text-sm text-gray-600">
          {isIos ? (
            <p>
              하단의 <Share size={14} className="inline text-brand-600" /> 공유 버튼을 누른 후{' '}
              <strong>"홈 화면에 추가"</strong>를 선택하면 앱처럼 사용할 수 있습니다.
              <br />
              <span className="text-xs text-gray-400 mt-1 block">
                * 홈 화면에서 실행해야 백그라운드 알림을 받을 수 있습니다.
              </span>
            </p>
          ) : (
            <>
              <p className="mb-3">홈 화면에 추가하면 앱처럼 사용할 수 있습니다.</p>
              <button
                onClick={handleInstall}
                className="w-full px-3 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                홈 화면에 추가
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
